import {
  parseNewsFeedDoc,
  parseStreamingDefinitionV2,
  type NewsFeedDoc,
  type StreamingDefinitionV2,
} from "@orvis/core";
import type {
  FirestoreLike,
  Unsubscribe,
} from "./types.js";

/**
 * All Firestore reads flow through here. Each function accepts an injected
 * FirestoreLike (see ./types.ts) and a country code, and returns V2 parsed
 * data — which is also valid V1 data for the legacy apps.
 *
 * Everything is idempotent: reading a missing doc returns `undefined` rather
 * than throwing, so callers can render an empty state consistently.
 */

async function readDoc(fs: FirestoreLike, country: string, docId: string): Promise<unknown | undefined> {
  const snap = await fs.collection(country).doc(docId).get();
  if (!snap.exists) return undefined;
  return snap.data();
}

export async function getChannels(fs: FirestoreLike, country: string): Promise<StreamingDefinitionV2 | undefined> {
  const raw = await readDoc(fs, country, "channels");
  return raw === undefined ? undefined : parseStreamingDefinitionV2(raw);
}

export async function getRadios(fs: FirestoreLike, country: string): Promise<StreamingDefinitionV2 | undefined> {
  const raw = await readDoc(fs, country, "radio");
  return raw === undefined ? undefined : parseStreamingDefinitionV2(raw);
}

export async function getNews(fs: FirestoreLike, country: string): Promise<NewsFeedDoc | undefined> {
  const raw = await readDoc(fs, country, "news");
  return raw === undefined ? undefined : parseNewsFeedDoc(raw);
}

/**
 * Subscribe to live updates for `{country}/{docId}`. Returns an unsubscribe
 * function. Errors from the underlying stream are surfaced to `onError` if
 * provided.
 */
export function subscribeChannels(
  fs: FirestoreLike,
  country: string,
  onData: (data: StreamingDefinitionV2 | undefined) => void,
  onError?: (err: unknown) => void
): Unsubscribe {
  return fs.collection(country).doc("channels").onSnapshot(
    (snap) => onData(snap.exists ? parseStreamingDefinitionV2(snap.data()) : undefined),
    onError
  );
}

export function subscribeRadios(
  fs: FirestoreLike,
  country: string,
  onData: (data: StreamingDefinitionV2 | undefined) => void,
  onError?: (err: unknown) => void
): Unsubscribe {
  return fs.collection(country).doc("radio").onSnapshot(
    (snap) => onData(snap.exists ? parseStreamingDefinitionV2(snap.data()) : undefined),
    onError
  );
}
